import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/* ── Lightweight toast system ── */
let toastId = 0;
const toastStore = { listeners: [], toasts: [] };
const addToast = (msg, type = "success") => {
  const id = ++toastId;
  const t = { id, msg, type };
  toastStore.toasts = [...toastStore.toasts, t];
  toastStore.listeners.forEach((fn) => fn([...toastStore.toasts]));
  setTimeout(() => {
    toastStore.toasts = toastStore.toasts.filter((x) => x.id !== id);
    toastStore.listeners.forEach((fn) => fn([...toastStore.toasts]));
  }, 4000);
};
const useToasts = () => {
  const [list, setList] = React.useState(toastStore.toasts);
  React.useEffect(() => {
    toastStore.listeners.push(setList);
    return () => { toastStore.listeners = toastStore.listeners.filter((fn) => fn !== setList); };
  }, []);
  return list;
};
const ToastContainer = () => {
  const toasts = useToasts();
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", top: 20, right: 20, zIndex: 99999, display: "flex", flexDirection: "column", gap: 8 }}>
      {toasts.map((t) => (
        <div
          key={t.id}
          style={{
            padding: "12px 20px",
            borderRadius: 10,
            color: "white",
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
            animation: "slideIn 0.3s ease-out",
            background: t.type === "error" ? "#dc2626" : t.type === "warning" ? "#d97706" : "#7c3aed",
          }}
        >
          {t.msg}
        </div>
      ))}
      <style>{`@keyframes slideIn{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}`}</style>
    </div>
  );
};

const STATUS_LABELS = {
  nueva: { label: "Nueva", color: "bg-blue-100 text-blue-800" },
  revisada: { label: "Revisada", color: "bg-gray-100 text-gray-800" },
  seguimiento: { label: "Seguimiento", color: "bg-yellow-100 text-yellow-800" },
  resuelta: { label: "Resuelta", color: "bg-green-100 text-green-800" },
};

const CallsManager = () => {
  const [tab, setTab] = useState("calls");
  const [status, setStatus] = useState(null);
  const [calls, setCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [callNotes, setCallNotes] = useState("");
  const [callStatus, setCallStatus] = useState("nueva");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [clients, setClients] = useState([]);
  const [outboundCalls, setOutboundCalls] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", phone: "", email: "", interest: "" });
  const [showCallModal, setShowCallModal] = useState(null);
  const [callMessage, setCallMessage] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cms/vapi/status`, authHeaders());
      setStatus(r.data);
      setCustomPrompt(r.data.custom_prompt || "");
    } catch (e) {
      console.error("Error loading Vapi status:", e);
    }
  }, []);

  const loadCalls = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cms/vapi/calls?limit=100`, authHeaders());
      setCalls(r.data);
    } catch (e) {
      console.error("Error loading calls:", e);
    }
  }, []);

  const loadPhoneNumbers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cms/vapi/phone-numbers`, authHeaders());
      setPhoneNumbers(r.data);
    } catch (e) {
      console.error("Error loading phone numbers:", e);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadStatus(), loadCalls(), loadPhoneNumbers()]).finally(() =>
      setLoading(false)
    );
  }, [loadStatus, loadCalls, loadPhoneNumbers]);

  const loadClients = async () => {
    try {
      const r = await axios.get(`${API}/cms/vapi/interested-clients`, authHeaders());
      setClients(r.data);
    } catch {}
  };

  const loadOutboundCalls = async () => {
    try {
      const r = await axios.get(`${API}/cms/vapi/outbound-calls`, authHeaders());
      setOutboundCalls(r.data);
    } catch {}
  };

  const handleSetup = async () => {
    setSettingUp(true);
    try {
      await axios.post(`${API}/cms/vapi/setup`, {}, authHeaders());
      await loadStatus();
      addToast("Asistente configurado correctamente");
    } catch (e) {
      addToast("Error: " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setSettingUp(false);
    }
  };

  const handleSyncProperties = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/cms/vapi/sync-properties`, {}, authHeaders());
      addToast(r.data.message);
      await loadStatus();
    } catch (e) {
      addToast("Error: " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setSyncing(false);
    }
  };

  const handleSavePrompt = async () => {
    setSavingPrompt(true);
    try {
      await axios.put(`${API}/cms/vapi/prompt`, { custom_prompt: customPrompt }, authHeaders());
      addToast("Prompt actualizado correctamente");
      await loadStatus();
    } catch (e) {
      addToast("Error: " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleAssignPhone = async (phoneId) => {
    try {
      await axios.post(`${API}/cms/vapi/phone-numbers/${phoneId}/assign`, {}, authHeaders());
      addToast("Número asignado al asistente");
      await loadStatus();
      await loadPhoneNumbers();
    } catch (e) {
      addToast("Error: " + (e.response?.data?.detail || e.message), "error");
    }
  };

  const openCallDetail = async (callId) => {
    try {
      const r = await axios.get(`${API}/cms/vapi/calls/${callId}`, authHeaders());
      setSelectedCall(r.data);
      setCallNotes(r.data.notes || "");
      setCallStatus(r.data.localStatus || "nueva");
    } catch (e) {
      addToast("Error cargando detalle", "error");
    }
  };

  const handleDeleteCall = async (callId, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta llamada de la lista?")) return;
    try {
      await axios.delete(`${API}/cms/vapi/calls/${callId}`, authHeaders());
      addToast("Llamada eliminada");
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(callId); return n; });
      await loadCalls();
    } catch (err) {
      addToast("Error al eliminar", "error");
    }
  };

  const toggleSelect = (callId) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(callId)) n.delete(callId); else n.add(callId);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === calls.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(calls.map((c) => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.size} llamada(s) seleccionada(s)?`)) return;
    try {
      await Promise.all([...selectedIds].map((id) => axios.delete(`${API}/cms/vapi/calls/${id}`, authHeaders())));
      addToast(`${selectedIds.size} llamada(s) eliminada(s)`);
      setSelectedIds(new Set());
      await loadCalls();
    } catch (err) {
      addToast("Error al eliminar", "error");
    }
  };

  const handleUpdateCallStatus = async () => {
    if (!selectedCall) return;
    try {
      await axios.put(
        `${API}/cms/vapi/calls/${selectedCall.id}/status`,
        { status: callStatus, notes: callNotes },
        authHeaders()
      );
      await loadCalls();
      setSelectedCall((prev) => ({ ...prev, localStatus: callStatus, notes: callNotes }));
      addToast("Estado actualizado");
    } catch (e) {
      addToast("Error: " + (e.response?.data?.detail || e.message), "error");
    }
  };

  const handleAddClient = async () => {
    if (!newClient.name || !newClient.phone) return addToast("Nombre y telefono obligatorios", "error");
    try {
      await axios.post(`${API}/cms/vapi/interested-clients`, newClient, authHeaders());
      addToast("Cliente añadido");
      setNewClient({ name: "", phone: "", email: "", interest: "" });
      setShowAddClient(false);
      await loadClients();
    } catch (e) {
      addToast("Error: " + (e.response?.data?.detail || e.message), "error");
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await axios.delete(`${API}/cms/vapi/interested-clients/${id}`, authHeaders());
      addToast("Cliente eliminado");
      await loadClients();
    } catch {}
  };

  const handleOutboundCall = async (client) => {
    const msg = callMessage || "";
    try {
      await axios.post(`${API}/cms/vapi/outbound-call`, { phone: client.phone, name: client.name, email: client.email || "", interest: client.interest || "", message: msg }, authHeaders());
      addToast("Llamada iniciada a " + client.name);
      setShowCallModal(null);
      setCallMessage("");
      await loadOutboundCalls();
    } catch (e) {
      addToast("Error: " + (e.response?.data?.detail || e.message), "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="calls-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="calls-manager" className="space-y-6">
      <ToastContainer />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Llamadas IA (Vapi)</h2>
        <div className="flex gap-2">
          {status?.configured && (
            <button
              onClick={handleSyncProperties}
              disabled={syncing}
              data-testid="sync-properties-btn"
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 text-sm font-medium"
            >
              {syncing ? "Sincronizando..." : "Sincronizar Propiedades"}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4" data-testid="calls-tabs">
          {[
            { id: "calls", label: "Llamadas" },
            { id: "clients", label: "Clientes interesados" },
            { id: "outbound", label: "Llamadas salientes" },
            { id: "config", label: "Configuracion" },
            { id: "phone", label: "Telefono" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? "border-purple-600 text-purple-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ─── Calls Tab ─── */}
      {tab === "calls" && (
        <div className="space-y-4">
          {!status?.configured && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-testid="setup-banner">
              <p className="text-yellow-800 font-medium">El asistente aun no esta configurado.</p>
              <p className="text-yellow-700 text-sm mt-1">
                Ve a la pestana "Configuracion" para crear el asistente de Vivendius.
              </p>
            </div>
          )}

          {calls.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border" data-testid="no-calls">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Sin llamadas</h3>
              <p className="mt-2 text-gray-500">
                Cuando los clientes llamen al numero asignado, las llamadas apareceran aqui.
              </p>
            </div>
          ) : (
            <>
              {/* Bulk actions bar */}
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3" data-testid="bulk-bar">
                  <span className="text-sm font-medium text-purple-700">{selectedIds.size} seleccionada(s)</span>
                  <button onClick={handleBulkDelete} data-testid="bulk-delete-btn" className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600">Eliminar seleccionadas</button>
                  <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300">Deseleccionar</button>
                </div>
              )}

              {/* Desktop table */}
              <div className="hidden md:block bg-white rounded-lg border overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200" data-testid="calls-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 w-10"><input type="checkbox" checked={selectedIds.size === calls.length && calls.length > 0} onChange={toggleSelectAll} className="rounded border-gray-300 text-purple-600 cursor-pointer" /></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefono</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duracion</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resumen</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {calls.map((call) => {
                      const st = STATUS_LABELS[call.localStatus] || STATUS_LABELS.nueva;
                      return (
                        <tr key={call.id} className={`hover:bg-gray-50 ${selectedIds.has(call.id) ? "bg-purple-50" : ""}`} data-testid={`call-row-${call.id}`}>
                          <td className="px-3 py-3"><input type="checkbox" checked={selectedIds.has(call.id)} onChange={() => toggleSelect(call.id)} className="rounded border-gray-300 text-purple-600 cursor-pointer" /></td>
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {call.createdAt ? new Date(call.createdAt).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {call.customerNumber}
                            {call.knownClient && (
                              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium" title={`${call.knownClient.name} | ${call.knownClient.email || ''} | ${call.knownClient.interest || ''}`}>
                                {call.knownClient.name || "Cliente conocido"}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{call.duration}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{call.summary || "Sin resumen"}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button onClick={() => openCallDetail(call.id)} data-testid={`view-call-${call.id}`} className="text-purple-600 hover:text-purple-800 text-sm font-medium mr-3">Ver detalle</button>
                            <button onClick={(e) => handleDeleteCall(call.id, e)} data-testid={`delete-call-${call.id}`} className="text-red-400 hover:text-red-600 text-sm" title="Eliminar">
                              <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3" data-testid="calls-mobile">
                {calls.map((call) => {
                  const st = STATUS_LABELS[call.localStatus] || STATUS_LABELS.nueva;
                  return (
                    <div key={call.id} className={`bg-white rounded-lg border p-4 space-y-2 ${selectedIds.has(call.id) ? "ring-2 ring-purple-400" : ""}`} data-testid={`call-card-${call.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={selectedIds.has(call.id)} onChange={() => toggleSelect(call.id)} className="rounded border-gray-300 text-purple-600 cursor-pointer" />
                          <span className="text-sm font-medium text-gray-900">
                            {call.createdAt ? new Date(call.createdAt).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>{call.customerNumber}</span>
                        {call.knownClient && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">{call.knownClient.name || "Conocido"}</span>}
                        <span>{call.duration}</span>
                      </div>
                      {call.summary && (
                        <p className="text-sm text-gray-500 line-clamp-2">{call.summary}</p>
                      )}
                      <div className="flex items-center justify-between pt-1 border-t">
                        <button onClick={() => openCallDetail(call.id)} className="text-purple-600 hover:text-purple-800 text-sm font-medium">Ver detalle</button>
                        <button onClick={(e) => handleDeleteCall(call.id, e)} className="text-red-400 hover:text-red-600 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Call Detail Modal */}
          {selectedCall && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" data-testid="call-detail-modal">
              <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
                <div className="p-6 border-b flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">Detalle de llamada</h3>
                  <button onClick={() => setSelectedCall(null)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Fecha</label>
                      <p className="text-sm font-medium">
                        {selectedCall.createdAt ? new Date(selectedCall.createdAt).toLocaleString("es-ES") : "—"}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Duracion</label>
                      <p className="text-sm font-medium">{selectedCall.duration}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Telefono cliente</label>
                      <p className="text-sm font-medium">{selectedCall.customerNumber}</p>
                      {selectedCall.knownClient && (
                        <div className="mt-1 p-2 bg-green-50 rounded-lg text-xs">
                          <span className="font-medium text-green-700">Cliente recurrente: </span>
                          <span className="text-green-800">{selectedCall.knownClient.name}</span>
                          {selectedCall.knownClient.email && <span className="text-green-600"> | {selectedCall.knownClient.email}</span>}
                          {selectedCall.knownClient.interest && <span className="text-green-600"> | {selectedCall.knownClient.interest}</span>}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Motivo de fin</label>
                      <p className="text-sm font-medium">{selectedCall.endedReason || "—"}</p>
                    </div>
                  </div>

                  {selectedCall.summary && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Resumen IA</label>
                      <p className="mt-1 text-sm bg-purple-50 p-3 rounded-lg">{selectedCall.summary}</p>
                    </div>
                  )}

                  {selectedCall.structuredData && Object.keys(selectedCall.structuredData).length > 0 && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Datos del cliente</label>
                      <div className="mt-1 bg-blue-50 p-3 rounded-lg text-sm space-y-1">
                        {Object.entries(selectedCall.structuredData).map(([key, val]) =>
                          val ? (
                            <p key={key}>
                              <span className="font-medium">{key}:</span> {String(val)}
                            </p>
                          ) : null
                        )}
                      </div>
                    </div>
                  )}

                  {selectedCall.transcript && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Transcripcion</label>
                      <div className="mt-1 bg-gray-50 p-3 rounded-lg text-sm max-h-48 overflow-y-auto whitespace-pre-wrap">
                        {selectedCall.transcript}
                      </div>
                    </div>
                  )}

                  {selectedCall.recordingUrl && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Grabacion</label>
                      <audio controls src={selectedCall.recordingUrl} className="mt-1 w-full" />
                    </div>
                  )}

                  <hr />

                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Estado de seguimiento</label>
                      <select
                        value={callStatus}
                        onChange={(e) => setCallStatus(e.target.value)}
                        data-testid="call-status-select"
                        className="mt-1 block w-full border rounded-lg px-3 py-2 text-sm"
                      >
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Notas internas</label>
                      <textarea
                        value={callNotes}
                        onChange={(e) => setCallNotes(e.target.value)}
                        rows={3}
                        data-testid="call-notes-input"
                        className="mt-1 block w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="Escribe notas sobre esta llamada..."
                      />
                    </div>
                    <button
                      onClick={handleUpdateCallStatus}
                      data-testid="save-call-status-btn"
                      className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 text-sm font-medium"
                    >
                      Guardar estado y notas
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Clients Tab ─── */}
      {tab === "clients" && (
        <div className="space-y-4" onFocus={() => {}} onMouseEnter={() => { if(!clients.length) loadClients(); }}>
          {!clients.length && <div style={{display:'none'}}>{setTimeout(() => loadClients(), 0) && ''}</div>}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Clientes interesados</h3>
            <button onClick={() => setShowAddClient(true)} data-testid="add-client-btn" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium">Añadir cliente</button>
          </div>

          {showAddClient && (
            <div className="bg-white rounded-lg border p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={newClient.name} onChange={(e) => setNewClient({...newClient, name: e.target.value})} placeholder="Nombre *" className="border rounded-lg px-3 py-2 text-sm" />
                <input value={newClient.phone} onChange={(e) => setNewClient({...newClient, phone: e.target.value})} placeholder="Telefono * (ej: +34612345678)" className="border rounded-lg px-3 py-2 text-sm" />
                <input value={newClient.email} onChange={(e) => setNewClient({...newClient, email: e.target.value})} placeholder="Email" className="border rounded-lg px-3 py-2 text-sm" />
                <input value={newClient.interest} onChange={(e) => setNewClient({...newClient, interest: e.target.value})} placeholder="Interes (ej: 2 hab Gracia)" className="border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleAddClient} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">Guardar</button>
                <button onClick={() => setShowAddClient(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">Cancelar</button>
              </div>
            </div>
          )}

          {clients.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No hay clientes interesados registrados.</p>
              <p className="text-gray-400 text-sm mt-1">Los datos se capturan automaticamente de las llamadas o puedes añadirlos manualmente.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {clients.map((c) => (
                <div key={c.id} className="bg-white rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{c.name || "Sin nombre"}</p>
                        {c.total_calls > 1 && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">{c.total_calls} llamadas</span>}
                        {c.notified && <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Notificado</span>}
                      </div>
                      <p className="text-sm text-gray-600">{c.phone} {c.email && `| ${c.email}`}</p>
                      {c.interest && <p className="text-xs text-purple-600 mt-1">Interes: {c.interest}</p>}
                      {c.last_call && <p className="text-xs text-gray-400 mt-1">Ultima llamada: {new Date(c.last_call).toLocaleString("es-ES")}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setShowCallModal(c); setCallMessage(""); }} className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600">Llamar</button>
                      <button onClick={() => handleDeleteClient(c.id)} className="text-red-400 hover:text-red-600 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                  {c.call_history && c.call_history.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Ver historial de llamadas ({c.call_history.length})</summary>
                      <div className="mt-2 space-y-1 pl-3 border-l-2 border-purple-200">
                        {c.call_history.map((h, i) => (
                          <div key={i} className="text-xs text-gray-600">
                            <span className="font-medium">{h.date ? new Date(h.date).toLocaleString("es-ES") : "?"}</span>
                            <span className="ml-2 text-gray-400">{h.type === "entrante" ? "Entrante" : "Saliente"}</span>
                            <span className="ml-2">{h.duration}</span>
                            {h.summary && <p className="text-gray-500 mt-0.5">{h.summary}</p>}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Call Modal */}
          {showCallModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-xl w-full max-w-md p-6 space-y-4">
                <h3 className="text-lg font-bold">Llamar a {showCallModal.name}</h3>
                <p className="text-sm text-gray-600">Numero: {showCallModal.phone}</p>
                <div>
                  <label className="text-sm text-gray-500">Mensaje personalizado (opcional)</label>
                  <textarea value={callMessage} onChange={(e) => setCallMessage(e.target.value)} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" placeholder="Ej: Hola, le llamo porque tenemos un piso nuevo en Gracia..." />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleOutboundCall(showCallModal)} className="flex-1 bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 font-medium">Iniciar llamada</button>
                  <button onClick={() => setShowCallModal(null)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200">Cancelar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Outbound Calls Tab ─── */}
      {tab === "outbound" && (
        <div className="space-y-4" onFocus={() => {}} onMouseEnter={() => { if(!outboundCalls.length) loadOutboundCalls(); }}>
          {!outboundCalls.length && <div style={{display:'none'}}>{setTimeout(() => loadOutboundCalls(), 0) && ''}</div>}
          <h3 className="text-lg font-bold text-gray-900">Historial de llamadas salientes</h3>
          {outboundCalls.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border">
              <p className="text-gray-500">No hay llamadas salientes registradas.</p>
              <p className="text-gray-400 text-sm mt-1">Las llamadas salientes se registran cuando llamas a un cliente o cuando el sistema notifica automaticamente.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {outboundCalls.map((c) => (
                <div key={c.id} className="bg-white rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{c.client_name || "Sin nombre"} - {c.phone}</p>
                      <p className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleString("es-ES") : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.status === "auto" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>
                        {c.status === "auto" ? "Automatica" : "Manual"}
                      </span>
                      <button onClick={async () => { if(!window.confirm("¿Eliminar esta llamada?")) return; try { await axios.delete(`${API}/cms/vapi/outbound-calls/${c.id}`, authHeaders()); addToast("Llamada eliminada"); await loadOutboundCalls(); } catch { addToast("Error", "error"); }}} className="text-red-400 hover:text-red-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                  {c.message && <p className="text-sm text-gray-600 mt-2">{c.message}</p>}
                  {c.property_title && <p className="text-xs text-purple-600 mt-1">Propiedad: {c.property_title}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Config Tab ─── */}
      {tab === "config" && (
        <div className="space-y-6">
          {/* Setup section */}
          <div className="bg-white rounded-lg border p-6" data-testid="config-section">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Asistente Vivendius</h3>

            {status?.configured ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-700">Asistente activo</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">ID:</span>{" "}
                    <span className="font-mono text-xs">{status.assistant_id}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Ultima sincronizacion:</span>{" "}
                    {status.last_synced ? new Date(status.last_synced).toLocaleString("es-ES") : "Nunca"}
                  </div>
                </div>
                <button
                  onClick={handleSetup}
                  disabled={settingUp}
                  data-testid="reconfigure-btn"
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  {settingUp ? "Reconfigurando..." : "Reconfigurar asistente"}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600">
                  Crea el asistente virtual de Vivendius. Se configurara automaticamente con tus propiedades actuales
                  y un prompt en castellano/catalan.
                </p>
                <button
                  onClick={handleSetup}
                  disabled={settingUp}
                  data-testid="setup-btn"
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50"
                >
                  {settingUp ? "Configurando..." : "Crear Asistente Vivendius"}
                </button>
              </div>
            )}
          </div>

          {/* Custom Prompt */}
          <div className="bg-white rounded-lg border p-6" data-testid="prompt-section">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Prompt personalizado</h3>
            <p className="text-gray-500 text-sm mb-4">
              Personaliza las instrucciones del asistente. Si lo dejas vacio, se usara el prompt por defecto
              (informacion de Vivendius, atencion en castellano/catalan, horarios, etc.).
            </p>
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={12}
              data-testid="custom-prompt-input"
              className="w-full border rounded-lg px-4 py-3 text-sm font-mono"
              placeholder="Deja vacío para usar el prompt por defecto de Vivendius..."
            />
            <div className="flex justify-end mt-3">
              <button
                onClick={handleSavePrompt}
                disabled={savingPrompt}
                data-testid="save-prompt-btn"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-50"
              >
                {savingPrompt ? "Guardando..." : "Guardar prompt"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Phone Tab ─── */}
      {tab === "phone" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6" data-testid="phone-section">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Numero de telefono</h3>

            {status?.phone_number ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-700 font-medium">Numero asignado al asistente</span>
                </div>
                <p className="text-lg font-mono">{status.phone_number.number}</p>
                <p className="text-sm text-gray-500">Proveedor: {status.phone_number.provider}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800 text-sm font-medium">Como configurar el numero de telefono:</p>
                  <ol className="mt-2 text-blue-700 text-sm space-y-1 list-decimal list-inside">
                    <li>Ve al <a href="https://dashboard.vapi.ai" target="_blank" rel="noreferrer" className="underline font-medium">Dashboard de Vapi</a></li>
                    <li>En el menu lateral, haz clic en <strong>"Phone Numbers"</strong></li>
                    <li>Crea un numero gratuito (EEUU) para pruebas o importa un numero de Twilio/Telnyx para España (+34)</li>
                    <li>Vuelve aqui y asigna el numero al asistente Vivendius</li>
                  </ol>
                </div>

                {phoneNumbers.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Numeros disponibles:</h4>
                    <div className="space-y-2">
                      {phoneNumbers.map((pn) => (
                        <div key={pn.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <div>
                            <p className="font-mono text-sm">{pn.number}</p>
                            <p className="text-xs text-gray-500">{pn.provider}</p>
                          </div>
                          <button
                            onClick={() => handleAssignPhone(pn.id)}
                            data-testid={`assign-phone-${pn.id}`}
                            className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                          >
                            Asignar a Vivendius
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No hay numeros de telefono en tu cuenta de Vapi. Crea uno desde el dashboard.
                  </p>
                )}

                <button
                  onClick={loadPhoneNumbers}
                  data-testid="refresh-phones-btn"
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  Actualizar lista de numeros
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CallsManager;
